import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { ValidationRule, FormValidator } from '../../utils/validation';

interface ValidatedInputProps {
  label: string;
  name: string;
  type?: 'text' | 'email' | 'password' | 'tel' | 'number';
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  validationRules?: ValidationRule;
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
  showValidation?: boolean;
  autoComplete?: string;
}

const ValidatedInput: React.FC<ValidatedInputProps> = ({
  label,
  name,
  type = 'text',
  value = '',
  onChange,
  onBlur,
  placeholder,
  icon,
  disabled = false,
  required = false,
  className = '',
  validationRules,
  validateOnChange = false,
  validateOnBlur = true,
  showValidation = true,
  autoComplete,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [error, setError] = useState<string>('');
  const [touched, setTouched] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const validator = validationRules
    ? new FormValidator({ [name]: validationRules })
    : null;

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const validateField = (val: string) => {
    if (!validator) return '';

    const result = validator.validate({ [name]: val });
    const fieldError = result.errors.find(err => err.field === name);
    return fieldError?.message || '';
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange?.(newValue);

    if (validateOnChange && touched) {
      const validationError = validateField(newValue);
      setError(validationError);
    }
  };

  const handleBlur = () => {
    setTouched(true);
    setIsFocused(false);
    onBlur?.();

    if (validateOnBlur) {
      const validationError = validateField(inputValue);
      setError(validationError);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const hasError = touched && error;
  const isValid = touched && !error && inputValue.length > 0;
  const inputType = type === 'password' && showPassword ? 'text' : type;

  const getInputClasses = () => {
    let classes =
      'w-full px-4 py-3 rounded-2xl border-2 transition-all duration-300 focus:outline-none ';

    if (icon) {
      classes += 'pl-12 ';
    }

    if (type === 'password') {
      classes += 'pr-12 ';
    }

    if (disabled) {
      classes += 'bg-gray-100 cursor-not-allowed ';
    }

    if (hasError) {
      classes += 'border-red-300 focus:border-red-500 bg-red-50/50 ';
    } else if (isValid && showValidation) {
      classes += 'border-green-300 focus:border-green-500 bg-green-50/50 ';
    } else if (isFocused) {
      classes += 'border-purple-500 bg-purple-50/50 ';
    } else {
      classes += 'border-gray-200 hover:border-gray-300 ';
    }

    return classes;
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <label htmlFor={name} className="block text-sm font-medium text-gray-700">
        {label}
        {(required || validationRules?.required) && (
          <span className="text-red-500 ml-1">*</span>
        )}
      </label>

      <div className="relative">
        {icon && (
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <div className="text-gray-400">{icon}</div>
          </div>
        )}

        <input
          id={name}
          name={name}
          type={inputType}
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          className={getInputClasses()}
          aria-invalid={hasError ? 'true' : 'false'}
          aria-describedby={hasError ? `${name}-error` : undefined}
        />

        {type === 'password' && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-0 pr-4 flex items-center"
            disabled={disabled}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? (
              <EyeOff className="w-5 h-5 text-gray-400 hover:text-gray-600" />
            ) : (
              <Eye className="w-5 h-5 text-gray-400 hover:text-gray-600" />
            )}
          </button>
        )}

        {showValidation && type !== 'password' && (
          <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
            {hasError && <AlertCircle className="w-5 h-5 text-red-500" />}
            {isValid && <CheckCircle className="w-5 h-5 text-green-500" />}
          </div>
        )}
      </div>

      <AnimatePresence>
        {hasError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-2 text-red-600 text-sm"
            id={`${name}-error`}
            role="alert"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ValidatedInput;
